import React, { memo, ReactNode } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { LoadingButton, LoadingButtonProps } from '@mui/lab'

export type ButtonSize = 'large' | 'medium' | 'small' | 'tiny'
export type ButtonColor =
  | 'primary'
  | 'light'
  | 'dark'
  | 'grey'
  | 'black'
  | 'white'
  | 'red'
  | 'green'

interface Props extends Omit<LoadingButtonProps, 'color' | 'size'> {
  children?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  size?: ButtonSize
  color?: ButtonColor
  round?: boolean
  selected?: boolean
  className?: any
  /** Square, text-free button — pairs with `leftIcon` and no `children`. */
  iconOnly?: boolean
}

const sizeStyles = {
  large: tw`h-[56px]`,
  medium: tw`h-[42px]`,
  small: tw`h-[36px]`,
  tiny: tw`h-[28px] rounded`,
}

const iconOnlySizeStyles = {
  large: tw`min-w-[56px] w-[56px] p-0`,
  medium: tw`min-w-[42px] w-[42px] p-0`,
  small: tw`min-w-[36px] w-[36px] p-0`,
  tiny: tw`min-w-[28px] w-[28px] p-0`,
}

// Ported from apps/dashboard's Button so the two apps look identical, but expressed with the
// CSS-variable-backed Tailwind tokens instead of dashboard's inline hex literals. The
// `disabled:` variants come from dashboard too: they hold the background steady so a disabled
// button dims via opacity on its content rather than washing out to MUI's default grey.
const colorsStyles = {
  primary: tw`bg-primary-500 disabled:bg-primary-500 hover:bg-primary-600 active:bg-primary-700`,
  light: tw`bg-primary-100 disabled:bg-primary-100 hover:bg-primary-200 active:bg-primary-300`,
  dark: tw`bg-primary-700 disabled:bg-primary-700 hover:bg-primary-900 active:bg-shades-1000`,
  grey: tw`bg-shades-100 disabled:bg-shades-100 hover:bg-shades-200 active:bg-shades-300`,
  black: tw`bg-shades-1000 disabled:bg-shades-1000 hover:bg-shades-800 active:bg-shades-900`,
  white: tw`bg-shades-0 disabled:bg-shades-0 hover:bg-shades-50 active:bg-shades-100 border border-solid border-shades-100 hover:border-shades-200 active:border-shades-200`,
  red: tw`bg-critical-50 disabled:bg-critical-50 hover:bg-critical-100 active:bg-critical-400`,
  green: tw`bg-success-200 disabled:bg-success-200 hover:bg-success-300 active:bg-success-400`,
}

const selectedColorsStyles = {
  primary: tw`bg-primary-700`,
  light: tw`bg-primary-300`,
  dark: tw`bg-primary-900`,
  grey: tw`bg-shades-300`,
  black: tw`bg-shades-900`,
  white: tw`bg-shades-100`,
  red: tw`bg-critical-400`,
  green: tw`bg-success-400`,
}

export const iconStyles = {
  primary: tw`fill-shades-0`,
  light: tw`fill-primary-500`,
  dark: tw`fill-shades-0`,
  grey: tw`fill-shades-1000`,
  black: tw`fill-shades-0`,
  white: tw`fill-shades-1000`,
  red: tw`fill-critical-500`,
  green: tw`fill-success-500`,
}

const textColorsStyles = {
  primary: tw`text-shades-0`,
  light: tw`text-primary-500`,
  dark: tw`text-shades-0`,
  grey: tw`text-shades-1000`,
  black: tw`text-shades-0`,
  white: tw`text-shades-1000`,
  red: tw`text-critical-500`,
  green: tw`text-success-500`,
}

const textSizeStyles = {
  large: tw`text-[16px]`,
  medium: tw`text-[16px]`,
  small: tw`text-[14px]`,
  tiny: tw`text-[14px]`,
}

const ButtonComponent = ({
  children,
  leftIcon,
  rightIcon,
  className,
  round,
  selected,
  disabled,
  loading,
  iconOnly,
  size = 'large',
  color = 'primary',
  ...rest
}: Props) => (
  <LoadingButton
    {...rest}
    loading={loading}
    disabled={disabled}
    css={[
      tw`min-w-0 flex-shrink-0 px-4`,
      !children && tw`px-2`,
      iconOnly && [iconOnlySizeStyles[size], tw`flex items-center justify-center`],
      sizeStyles[size],
      colorsStyles[color],
      selected && selectedColorsStyles[color],
      round && tw`rounded-[40px]`,
      className,
    ]}
  >
    {leftIcon && !loading ? (
      <div css={[tw`mr-2 h-5`, disabled && tw`opacity-50`, !children && tw`mr-0`]}>{leftIcon}</div>
    ) : (
      <div css={children && tw`w-1`} />
    )}
    {children && (
      <Typography
        css={[
          tw`font-semibold`,
          textColorsStyles[color],
          textSizeStyles[size],
          disabled && tw`opacity-50`,
          loading && tw`opacity-0`,
        ]}
      >
        {children}
      </Typography>
    )}
    {rightIcon && !loading ? (
      <div css={[tw`ml-2 h-5`, disabled && tw`opacity-50`, !children && tw`ml-0`]}>{rightIcon}</div>
    ) : (
      <div css={children && tw`w-1`} />
    )}
  </LoadingButton>
)

export const Button = memo(ButtonComponent)
